import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="pp-page">
      <div className="pp-wrapper">
        {/* Header */}
        <div className="pp-header-section">
          <h1 className="pp-main-title">Privacy Policy</h1>
          <p className="pp-last-updated">Last Updated: January 22, 2025</p>
        </div>

        {/* Introduction */}
        <section className="pp-content-section">
          <p className="pp-intro-text">
            At Kitty n' Mice, we are committed to protecting your privacy and
            ensuring the security of your personal information. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your
            information when you visit our website or make a purchase from our
            store.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">1. Information We Collect</h2>

          <h3 className="pp-subsection-title">Personal Information</h3>
          <p className="pp-text">
            When you make a purchase or create an account, we collect:
          </p>
          <ul className="pp-list">
            <li className="pp-list-item">
              Name and contact information (email address, phone number)
            </li>
            <li className="pp-list-item">Billing and shipping addresses</li>
            <li className="pp-list-item">
              Payment information (processed securely through our payment
              gateway)
            </li>
            <li className="pp-list-item">Order history and purchase details</li>
          </ul>

          <h3 className="pp-subsection-title">Non-Personal Information</h3>
          <p className="pp-text">
            We automatically collect certain information about your device,
            including:
          </p>
          <ul className="pp-list">
            <li className="pp-list-item">IP address and browser type</li>
            <li className="pp-list-item">
              Device information and operating system
            </li>
            <li className="pp-list-item">
              Pages visited and time spent on our website
            </li>
            <li className="pp-list-item">Referring website addresses</li>
            <li className="pp-list-item">
              Cookies and similar tracking technologies
            </li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">2. How We Use Your Information</h2>
          <p className="pp-text">We use the information we collect to:</p>
          <ul className="pp-list">
            <li className="pp-list-item">Process and fulfill your orders</li>
            <li className="pp-list-item">
              Send order confirmations and shipping updates
            </li>
            <li className="pp-list-item">
              Provide customer support and respond to inquiries
            </li>
            <li className="pp-list-item">
              Improve our website and shopping experience
            </li>
            <li className="pp-list-item">
              Send promotional emails about new products and special offers
              (with your consent)
            </li>
            <li className="pp-list-item">Prevent fraud and enhance security</li>
            <li className="pp-list-item">Comply with legal obligations</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">
            3. Information Sharing and Disclosure
          </h2>
          <p className="pp-text">
            We do not sell, trade, or rent your personal information to third
            parties. We may share your information with:
          </p>
          <ul className="pp-list">
            <li className="pp-list-item">
              <strong className="pp-bold">Service Providers:</strong> Payment
              processors, shipping companies, and email service providers who
              help us operate our business
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Legal Requirements:</strong> When
              required by law or to protect our rights and safety
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Business Transfers:</strong> In the
              event of a merger, acquisition, or sale of assets
            </li>
          </ul>
        </section>

        {/* Cookies and Tracking */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">
            4. Cookies and Tracking Technologies
          </h2>
          <p className="pp-text">We use cookies and similar technologies to:</p>
          <ul className="pp-list">
            <li className="pp-list-item">
              Remember your preferences and login information
            </li>
            <li className="pp-list-item">
              Analyze website traffic and user behavior
            </li>
            <li className="pp-list-item">
              Personalize your shopping experience
            </li>
            <li className="pp-list-item">Deliver targeted advertisements</li>
          </ul>
          <p className="pp-text">
            You can control cookies through your browser settings. Note that
            disabling cookies may affect website functionality.
          </p>
        </section>

        {/* Data Security */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">5. Data Security</h2>
          <p className="pp-text">
            We implement industry-standard security measures to protect your
            personal information, including:
          </p>
          <ul className="pp-list">
            <li className="pp-list-item">
              SSL encryption for all data transmission
            </li>
            <li className="pp-list-item">
              Secure payment processing through trusted payment gateways
            </li>
            <li className="pp-list-item">
              Regular security audits and updates
            </li>
            <li className="pp-list-item">Restricted access to personal data</li>
          </ul>
          <p className="pp-text">
            However, no method of transmission over the Internet is 100% secure,
            and we cannot guarantee absolute security.
          </p>
        </section>

        {/* Your Rights */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">6. Your Privacy Rights</h2>
          <p className="pp-text">You have the right to:</p>
          <ul className="pp-list">
            <li className="pp-list-item">
              <strong className="pp-bold">Access:</strong> Request a copy of the
              personal information we hold about you
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Correction:</strong> Update or correct
              inaccurate information
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Deletion:</strong> Request deletion of
              your personal information (subject to legal obligations)
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Opt-Out:</strong> Unsubscribe from
              marketing communications at any time
            </li>
            <li className="pp-list-item">
              <strong className="pp-bold">Data Portability:</strong> Request
              your data in a portable format
            </li>
          </ul>
          <p className="pp-text">
            To exercise these rights, please contact us at{" "}
            <a href="mailto:Kittynmice00@gmail.com" className="pp-link">
              Kittynmice00@gmail.com
            </a>
          </p>
        </section>

        {/* Return Policy */}
        <section className="pp-content-section pp-highlight-box">
          <h2 className="pp-section-title">7. Return Policy</h2>
          <div className="pp-return-notice">
            <div className="pp-notice-icon">🔄</div>
            <div className="pp-notice-text">
              <h3 className="pp-notice-heading">
                Important Return Policy Information
              </h3>
              <p className="pp-return-highlight">
                Returns must be initiated within{" "}
                <strong className="pp-bold-red">3 days of delivery</strong>.
                Products must be unused, unwashed, and in original packaging
                with all tags attached.
              </p>
              <p className="pp-text">To initiate a return:</p>
              <ul className="pp-list">
                <li className="pp-list-item">
                  Contact our customer service within 3 days of receiving your
                  order
                </li>
                <li className="pp-list-item">
                  Provide your order number and reason for return
                </li>
                <li className="pp-list-item">
                  Items must be in their original condition
                </li>
                <li className="pp-list-item">
                  Refunds will be processed within 7-10 business days after
                  receiving the returned item
                </li>
              </ul>
              <p className="pp-return-note">
                <strong className="pp-bold">Note:</strong> Items on sale,
                clearance products, and personalized items are not eligible for
                return. Shipping costs are non-refundable unless the return is
                due to our error.
              </p>
            </div>
          </div>
        </section>

        {/* Children's Privacy */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">8. Children's Privacy</h2>
          <p className="pp-text">
            While our products are designed for children, our website is
            intended for use by adults. We do not knowingly collect personal
            information from children under 13 without parental consent. If you
            believe we have collected information from a child under 13, please
            contact us immediately.
          </p>
        </section>

        {/* Third-Party Links */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">9. Third-Party Links</h2>
          <p className="pp-text">
            Our website may contain links to third-party websites. We are not
            responsible for the privacy practices of these external sites. We
            encourage you to review their privacy policies before providing any
            personal information.
          </p>
        </section>

        {/* Data Retention */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">10. Data Retention</h2>
          <p className="pp-text">
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this Privacy Policy, unless a
            longer retention period is required by law. When your information is
            no longer needed, we will securely delete or anonymize it.
          </p>
        </section>

        {/* International Transfers */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">11. International Data Transfers</h2>
          <p className="pp-text">
            Your information may be transferred to and processed in countries
            other than your country of residence. These countries may have
            different data protection laws. By using our website, you consent to
            such transfers.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="pp-content-section">
          <h2 className="pp-section-title">
            12. Changes to This Privacy Policy
          </h2>
          <p className="pp-text">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices or legal requirements. We will notify you
            of any significant changes by posting the updated policy on this
            page and updating the "Last Updated" date. We encourage you to
            review this policy periodically.
          </p>
        </section>

        {/* Contact Information */}
        <section className="pp-content-section pp-contact-box">
          <h2 className="pp-section-title">13. Contact Us</h2>
          <p className="pp-text">
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or our data practices, please contact us:
          </p>

          <div className="pp-contact-grid">
            <div className="pp-contact-card">
              <strong className="pp-contact-label">Email:</strong>
              <a href="mailto:Kittynmice00@gmail.com" className="pp-link">
                Kittynmice00@gmail.com
              </a>
            </div>
            <div className="pp-contact-card">
              <strong className="pp-contact-label">Customer Support:</strong>
              <a href="mailto:Kittynmice00@gmail.com" className="pp-link">
                Kittynmice00@gmail.com
              </a>
            </div>
            <div className="pp-contact-card">
              <strong className="pp-contact-label">Phone:</strong>
              <a href="tel:+91 80762 17714" className="pp-link">
                +91 80762 17714
              </a>
            </div>
            <div className="pp-contact-card">
              <strong className="pp-contact-label">Address:</strong>
              <p className="pp-text pp-address">
                Kitty n' Mice
                <br />
                Vikaspuri New Delhi 110018
                {/* <br />
                [City, State, PIN Code]
                <br /> */}
                <br />
                India
              </p>
            </div>
          </div>
        </section>

        {/* Footer Links */}
        <div className="pp-footer-section">
          <p className="pp-footer-text">Related Policies:</p>
          <div className="pp-footer-links">
            <Link to="/terms-and-conditions" className="pp-footer-link">
              Terms & Conditions
            </Link>
            <Link to="/shipping-policy" className="pp-footer-link">
              Shipping Policy
            </Link>
            <Link to="/refund-policy" className="pp-footer-link">
              Return Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
